import { observer } from "mobx-react-lite";
import { AlertTriangle, UserCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  useESGCollaborationStore,
  useESGDataEntryStore,
} from "@/features/esg/hooks/useESGReportStore";
import { useTran } from "@/hooks";
import { buildDelegationSummary } from "@/features/esg/utils/review-utils";

const ReviewDelegationsCard = observer(() => {
  const { t } = useTran();
  const { delegatedDisclosures } = useESGCollaborationStore();
  const { isDisclosureCompleted } = useESGDataEntryStore();

  const summary = buildDelegationSummary(
    delegatedDisclosures,
    isDisclosureCompleted,
  );

  const hasPendingDelegations = summary.some((g) => g.completed < g.total);

  return (
    <div className="h-full rounded-radius border border-border bg-card shadow-sm">
      {/* Header */}
      <div className="grid grid-cols-[1fr_auto] items-center border-b border-border p-4">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {t("Delegation inbox")}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("Track delegated disclosures and follow up.")}
          </p>
        </div>
        <Badge className="border border-amber-500/30 bg-amber-500/10 text-amber-600 text-xs">
          <UserCircle className="mr-1 h-3 w-3" />
          {summary.reduce((sum, g) => sum + g.total, 0)} {t("delegated")}
        </Badge>
      </div>

      {/* Delegatee list */}
      <div className="space-y-3 p-4">
        {summary.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted-foreground">
            {t("No delegations.")}
          </p>
        ) : (
          summary.map((group) => {
            const status =
              group.completed === group.total
                ? t("Completed")
                : group.completed > 0
                  ? t("In progress")
                  : t("Not started");
            const statusColor =
              group.completed === group.total
                ? "text-emerald-600"
                : group.completed > 0
                  ? "text-blue-600"
                  : "text-muted-foreground";

            return (
              <div
                key={group.email}
                className="rounded-radius border border-border bg-background p-3"
              >
                <div className="grid grid-cols-[1fr_auto] items-center">
                  <div className="flex items-center gap-2">
                    <UserCircle className="h-5 w-5 text-muted-foreground" />
                    <p className="text-sm font-medium text-foreground">
                      {group.email}
                    </p>
                  </div>
                  <Badge className="bg-muted text-foreground text-xs">
                    {group.total} {t("assigned")}
                  </Badge>
                </div>
                <p className={`mt-1 text-xs ${statusColor}`}>
                  {t("Status")}: {status}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {group.disclosures.map(({ code, completed }) => (
                    <Badge
                      key={code}
                      variant="outline"
                      className={`text-[11px] ${
                        completed
                          ? "border-emerald-500/50 text-emerald-600"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {code}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* PDF blocked warning */}
      {hasPendingDelegations && (
        <div className="px-4 pb-4">
          <div className="rounded-radius border border-amber-500/30 bg-amber-500/10 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                  {t("PDF export is blocked")}
                </p>
                <p className="text-[11px] text-amber-600 dark:text-amber-500">
                  {t(
                    "Delegated items must be completed to reach 100% and enable PDF generation.",
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default ReviewDelegationsCard;
